import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const formatDetail = (detail) => {
  if (!detail) return "Unable to sign in. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
};

export default function AdminLogin() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [values, setValues] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!values.email || !values.password) {
      toast.error("Please enter your email and password.");
      return;
    }
    setSubmitting(true);
    try {
      await login(values.email.trim().toLowerCase(), values.password);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Sign in failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center px-5 py-16"
      style={{
        background:
          "radial-gradient(1000px 500px at 80% -10%, rgba(163,196,211,0.35), transparent 60%), radial-gradient(900px 500px at -10% 10%, rgba(110,156,136,0.22), transparent 60%), linear-gradient(180deg, #FAFAF7 0%, #FFFFFF 100%)",
      }}
    >
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[var(--ew-text-2)] hover:text-[var(--ew-accent)] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} /> Back to site
        </Link>

        <div className="ew-card mt-4 p-8 sm:p-10">
          <div className="flex items-center gap-3">
            <span className="icon-badge">
              <Lock className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <div>
              <p className="eyebrow">Admin</p>
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[var(--ew-text)] leading-tight mt-1">
                Sign in to dashboard
              </h1>
            </div>
          </div>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" noValidate>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-[var(--ew-text)] mb-2"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                data-testid="admin-login-email"
                value={values.email}
                onChange={(e) =>
                  setValues((v) => ({ ...v, email: e.target.value }))
                }
                className="ew-input"
                placeholder="you@example.com"
                required
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-[var(--ew-text)] mb-2"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                data-testid="admin-login-password"
                value={values.password}
                onChange={(e) =>
                  setValues((v) => ({ ...v, password: e.target.value }))
                }
                className="ew-input"
                placeholder="••••••••"
                required
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={submitting}
              className="btn-primary w-full disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
